import Text, { Inline } from '../Text';
import { Flex, Padding } from '../layout-components';
import TableSortLabel from '@material-ui/core/TableSortLabel/TableSortLabel';
import _ from 'lodash';
import Icon from 'components/base/Icon';
import { Tooltip } from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import { boolPropsToClassNames } from 'common/utils/style.utils';
import {
  StyledColorIndicator,
  StyledExpandedSortByTooltip,
  StyledTableHeaderCell
} from './Table.styles';
import { LBButton } from '@linearb/buzz';

export const EXPAND_TYPES = {
  PARENT: 'parent',
  CHILD: 'child'
};

const SORT_TYPES = {
  CHILD_COLUMN: 'child_column',
  OWN_COLUMN: 'own_column',
  NOT_SORTED: 'not_sorted'
};

const ExpandToggler = ({ isOpen, toggleFn }) => {
  const iconName = isOpen ? 'ChevronLeft' : 'ChevronRight';
  return (
    <LBButton
      size='icon'
      variant='ghost'
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        toggleFn();
      }}
      role={`expand-col-toggler-${isOpen ? 'close' : 'open'}`}
    >
      <Icon name={iconName} size='medium' color='dodgerBlue' />
    </LBButton>
  );
};

const getLastExpendedChildColName = expandProps =>
  _.get(_.last(_.get(expandProps, 'columns')), 'name');

const getSortType = ({ column = {}, sortBy, expandType, isExpanded }) => {
  const sortByKey = column.sortPoint || column.dataPoint;

  if (expandType === EXPAND_TYPES.PARENT && !isExpanded) {
    // parent is not expanded -> should check if it's childs isSorted
    const expandedColumns = _.get(column, 'expandProps.columns', []);
    const isChildSorted = expandedColumns.some((childCol = {}) => {
      const childSortKey = childCol.sortPoint || childCol.dataPoint;
      return childSortKey === sortBy;
    });
    if (isChildSorted) return SORT_TYPES.CHILD_COLUMN;
  }

  return sortByKey === sortBy ? SORT_TYPES.OWN_COLUMN : SORT_TYPES.NOT_SORTED;
};

const SortByChildTooltip = ({ sortBy, sortOrder, column }) => {
  const childColumns = _.get(column, 'expandProps.columns', []);
  const sortedChild = childColumns.find(
    ({ sortPoint, dataPoint } = {}) =>
      sortPoint === sortBy || dataPoint === sortBy
  );
  const sortedChildName = _.get(sortedChild, 'name', '').toUpperCase();

  const IconSortOrder =
    sortOrder === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;

  return (
    <StyledExpandedSortByTooltip>
      <IconSortOrder />
      <Text>
        SORTING BY <span className='col-name'>{sortedChildName}</span>
      </Text>
    </StyledExpandedSortByTooltip>
  );
};

const ColHelpTooltip = ({ tooltip }) => {
  return (
    <Tooltip placement='top' title={tooltip}>
      <Padding l='x-small' t='x-x-small'>
        <Icon
          size='x-small'
          name='infooutlined'
          color='chambray'
          onClick={e => e.stopPropagation()}
        />
      </Padding>
    </Tooltip>
  );
};

const TableHeaderCell = ({ column, sortBy, sortOrder, onSort, expandType }) => {
  const { ColumnHeaderComponent = ({ children }) => children } = column;

  const expandProps =
    _.get(column, 'expandProps') || _.get(column, 'parentCol.expandProps');
  const isExpanded = _.get(expandProps, 'isOpen');

  const isLastExpendedChild =
    expandType === EXPAND_TYPES.CHILD &&
    getLastExpendedChildColName(expandProps) === _.get(column, 'name', false);

  const shouldShowExpandToggler =
    (expandType === EXPAND_TYPES.PARENT && !isExpanded) || isLastExpendedChild;

  const sortType = getSortType({ column, sortBy, expandType, isExpanded });
  const isSorted =
    sortType === SORT_TYPES.OWN_COLUMN || sortType === SORT_TYPES.CHILD_COLUMN;

  const columnName = _.get(column, 'name', '');
  const helpTooltip = _.get(column, 'help.tooltip');

  return (
    <StyledTableHeaderCell
      component={'div'}
      align={column.align || 'left'}
      className={boolPropsToClassNames({
        isSorted,
        isExpanded,
        isLastExpendedChild,
        isParent: expandType === EXPAND_TYPES.PARENT
      })}
      {...(column.tableCellProps ? column.tableCellProps : {})}
      {...(column.tableCellHeadProps ? column.tableCellHeadProps : {})}
    >
      <ColumnHeaderComponent>
        <Tooltip
          disableHoverListener={sortType !== SORT_TYPES.CHILD_COLUMN}
          placement='top'
          title={
            <SortByChildTooltip
              sortBy={sortBy}
              sortOrder={sortOrder}
              column={column}
            />
          }
          role='tooltip-sortby-child-col'
        >
          <Flex>
            {column.isSortedCol || isSorted ? (
              <TableSortLabel
                active={isSorted}
                onClick={() => {
                  if (column.isSortedCol) onSort();
                }}
                direction={!isSorted ? 'desc' : sortOrder}
              >
                <Inline
                  size='small'
                  className='col-header'
                  {...(isSorted ? { weight: 800 } : {})}
                >
                  <Flex direction='row' items='center' justify='space-between'>
                    {column.color && (
                      <Padding r='small'>
                        <StyledColorIndicator color={column.color} />
                      </Padding>
                    )}
                    {columnName}
                    {helpTooltip && <ColHelpTooltip tooltip={helpTooltip} />}
                    {shouldShowExpandToggler && (
                      <ExpandToggler
                        isOpen={expandProps.isOpen}
                        toggleFn={expandProps.toggleFn}
                      />
                    )}
                  </Flex>
                </Inline>
              </TableSortLabel>
            ) : (
              <Inline size='small' className='col-header'>
                <Flex direction='row' items='center' justify='space-between'>
                  {column.color && (
                    <Padding r='small'>
                      <StyledColorIndicator color={column.color} />
                    </Padding>
                  )}
                  {columnName}
                  {helpTooltip && <ColHelpTooltip tooltip={helpTooltip} />}
                  {shouldShowExpandToggler && (
                    <ExpandToggler
                      isOpen={expandProps.isOpen}
                      toggleFn={expandProps.toggleFn}
                    />
                  )}
                </Flex>
              </Inline>
            )}
          </Flex>
        </Tooltip>
      </ColumnHeaderComponent>
    </StyledTableHeaderCell>
  );
};

export default TableHeaderCell;
